import { InputHTMLAttributes, forwardRef } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  helpText?: string;
}

/**
 * Input component with label, error state, and help text.
 * Accessible: label linked via htmlFor, error via aria-describedby.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helpText, id, className = '', ...props }, ref) => {
    const inputId = id || `input-${label.toLowerCase().replace(/\s/g, '-')}`;
    const errorId = `${inputId}-error`;
    const helpId = `${inputId}-help`;

    return (
      <div className={`form-group ${error ? 'form-group-error' : ''} ${className}`}>
        <label htmlFor={inputId}>{label}</label>
        <input
          ref={ref}
          id={inputId}
          aria-invalid={!!error}
          aria-describedby={[error ? errorId : '', helpText ? helpId : ''].filter(Boolean).join(' ') || undefined}
          {...props}
        />
        {helpText && !error && (
          <small id={helpId}>{helpText}</small>
        )}
        {error && (
          <small id={errorId} className="form-error" role="alert">
            {error}
          </small>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
